import { useMutation, useQuery, useQueryClient } from 'react-query';
import { captureAppError } from '@leagueplatform/observability';
import {
  fetchCommunicationPreferences,
  updateCommunicationPreferences,
} from 'api/communincation-preferences';
import { SENTRY_CONSTANTS } from 'common/constants';
import { ApiGetData, ApiPatchData } from 'models/profile-settings.model';
import { useProfileToast } from 'hooks/use-profile-toast.hook';

type Arguments = {
  showNotification?: boolean;
};

export const useCommunicationPreferenceManager = ({
  showNotification = false,
}: Arguments) => {
  const queryClient = useQueryClient();
  const { onSuccess, onError } = useProfileToast();

  const {
    data,
    isLoading: isFetching,
    isError: fetchError,
    error: fetchErrorDetail,
  } = useQuery<ApiGetData | undefined, Error & { status?: number }>(
    ['get_communication_preferences'],
    fetchCommunicationPreferences,
    {
      staleTime: 5 * 60 * 1000, // 5 minutes cache
      retry: 1, // Only 1 retry to reduce delay
      refetchOnWindowFocus: false, // Don't refetch when window gains focus
      refetchOnMount: true, // Always refetch on mount to ensure fresh data

      onError: (error) => {
        captureAppError(error as Error, {
          ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
        });
      },
    },
  );

  const {
    mutateAsync: updatePreferences,
    isLoading: isUpdating,
    isError: updateError,
    isSuccess,
  } = useMutation({
    mutationFn: (payload: ApiPatchData) =>
      updateCommunicationPreferences(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['get_communication_preferences']);
      if (showNotification) {
        onSuccess('STR_PROFILE_COMMUNICATION_SUCCESS_MSG');
      }
    },
    onError: (error) => {
      if (showNotification) {
        onError();
      }
      captureAppError(error as Error, {
        ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
      });
    },
  });

  return {
    preferences: data,
    isFetching,
    fetchError,
    fetchErrorCode: fetchErrorDetail?.status,
    updatePreferences,
    isUpdating,
    updateError,
    isSuccess,
  };
};
